import { useSelector } from "react-redux";
import { RootState } from "./rootState";
import * as Actions from "./actions";
import { useCallback } from "react";
import { AppState, PatchType } from "./appState";
import { stringFromErrorOrAny } from "../utils/tools";
import { MessageBarType } from "@fluentui/react/lib/MessageBar";

export enum UIOperationState {
  NotStarted = "NotStarted",
  InProgress = "InProgress",
  Submitting = "Submitting",
  Completed = "Completed",
  Failed = "Failed",
  Cancelled = "Cancelled",
}

export class Operation<T = any> {
  state: UIOperationState = UIOperationState.NotStarted;
  message?: string;
  data?: T;
}

export const ticketOperationKey = (...args): string => operationKey(["Ticket", ...args]);

export const addNodeOperationKey = (ticketId: number, parentNodeId: number, postfix?: string): string =>
  ticketOperationKey(ticketId, PatchType.AddNode, "Under", parentNodeId, postfix ?? "");

export const updateNodeOperationKey = (ticketId: number, parentNodeId: number): string =>
  ticketOperationKey(ticketId, PatchType.ModifyNode, "Under", parentNodeId);

export const operationKey = (...args): string => args.join("=>");

// Whether to keep editors like ad solution open. We want to keep them open for InProgress & failed states
export const keepFormOpen = (operation: Operation): boolean =>
  operation?.state === UIOperationState.InProgress
  || operation?.state === UIOperationState.Failed
  || operation?.state === UIOperationState.Submitting;

// Whether the operation is in a state such that it can be sumitted (or resubmitted in case of failure)
export const isReadyToSubmit = (operation: Operation): boolean =>
  operation?.state == null
  || operation?.state === undefined
  || operation?.state === UIOperationState.NotStarted
  || operation?.state === UIOperationState.Failed
  || operation?.state === UIOperationState.Cancelled;

export const operationHasMessage = (operation: Operation): string => operation?.state !== UIOperationState.NotStarted && operation?.message;

export const operationIsSubmitting = (operation: Operation): boolean => operation?.state === UIOperationState.Submitting;

export const operationMesageBarType = (operation: Operation): MessageBarType => operation?.state === UIOperationState.Failed ? MessageBarType.error : MessageBarType.info;

export interface UseOperationResult<T = any> {
  operation: Operation<any>;
  key: string;
  isInProgress: () => boolean;
  setState: (state: UIOperationState) => void;
  setData: (data: T) => void;
  setMessage: (message: string) => void;
  setOperation: (state: UIOperationState, data: T, message: string) => void;
  resetOperation: () => void;
}

// hooks
export const useOperation = <T = any>(key: string): UseOperationResult<T> => {
  // eslint-disable-next-line security/detect-object-injection
  const operation = useSelector((state: RootState) => state.appState.operations[key]);

  const setState = useCallback((state: UIOperationState) => {
    Actions.setOperationState(key, state);
  }, [operation, key]);

  const resetOperation = useCallback(() => {
    Actions.setOperationState(key, UIOperationState.NotStarted, null, null);
  }, [operation, key]);

  const setData = useCallback((data: T) => {
    Actions.setOperationState(key, undefined, data);
  }, [operation, key]);

  const setMessage = useCallback((message: string) => {
    Actions.setOperationState(key, undefined, undefined, message);
  }, [operation, key]);

  const setOperation = useCallback((state: UIOperationState, data: T, message: string) => {
    Actions.setOperationState(key, state, data, message);
  }, [operation, key]);

  const isInProgress = useCallback(() => {
    return operation?.state === UIOperationState.InProgress || operation?.state === UIOperationState.Submitting;
  }, [operation, key]);

  return { operation, key, isInProgress, setState, setData, setMessage, setOperation, resetOperation };
}


// reducer helpers
export function operationStarted(draftState: AppState, key: string): void {
  if (!key) {
    console.warn(`Invalid operation key: '${key}'`);
    return;
  }

  // eslint-disable-next-line security/detect-object-injection
  if (!draftState.operations[key]) {
    // eslint-disable-next-line security/detect-object-injection
    draftState.operations[key] = {
      state: UIOperationState.InProgress,
      message: null
    } as Operation
  } else {
    // eslint-disable-next-line security/detect-object-injection
    draftState.operations[key].state = UIOperationState.InProgress;
    // eslint-disable-next-line security/detect-object-injection
    draftState.operations[key].message = null;
  }
}

export function operationSubmitting(draftState: AppState, key: string, message: string = undefined): void {
  if (!key) {
    console.warn(`Invalid operation key: '${key}'`);
    return;
  }

  // eslint-disable-next-line security/detect-object-injection
  const op = draftState.operations[key];
  op.state = UIOperationState.Submitting;
  op.message = message;
}

export function operationSuceeded(draftState: AppState, key: string, message: string = undefined): void {
  if (!key) {
    console.warn(`Invalid operation key: '${key}'`);
    return;
  }

  // eslint-disable-next-line security/detect-object-injection
  const op = draftState.operations[key];
  op.state = UIOperationState.Completed;
  op.message = message;
}

export function operationFailed(draftState: AppState, key: string, errors: string | string[] = undefined): void {
  if (!key) {
    console.warn(`Invalid operation key: '${key}'`);
    return;
  }

  // eslint-disable-next-line security/detect-object-injection
  const op = draftState.operations[key];
  op.state = UIOperationState.Failed;
  op.message = stringFromErrorOrAny(errors);
}


export function operationCancelled(draftState: AppState, key: string): void {
  if (!key) {
    console.warn(`Invalid operation key: '${key}'`);
    return;
  }

  // eslint-disable-next-line security/detect-object-injection
  const op = draftState.operations[key];
  op.state = UIOperationState.Cancelled;
}
